import { makeVar } from '@apollo/client'
import type { PickActionButtonVariant } from 'components/ActionBar/ActionButton'
import { processEnv } from 'components/configuration/environmentVariables'
import {
    AddNoteSpecifierHandler,
    AddNoteSpecifierProps,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/hooks'
import type {
    InsertPillHandler,
    InsertPillProps,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/hooks/useInsertPill'
import { InsertUOMPillProps } from 'pages/ProjectsPage/WorkspacePage/components/Common/hooks/useInsertUOMPill'
import type {
    NormalizedDocumentTemplateViewItem,
    PromoteDemoteRequirements,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/model'
import { createReactiveVarStore } from './createReactiveVarStore'
/*
workspaceTemplateData - 
It is a reactiveVar that stores the current template selected in the workspace
It is used to help the Workspace Action Bar MoveUp/MoveDown buttonsto indentify which paragraph is selected to do proper actions needed 
*/
export interface WorkspaceTemplateContent {
    workspaceTemplateData: NormalizedDocumentTemplateViewItem | undefined
}

export const [templateData, setWorkspaceTemplateData] = createReactiveVarStore<
    WorkspaceTemplateContent
>({
    workspaceTemplateData: undefined,
})

/*
promoteDemoteWorkspaceTemplateData - 
It is a reactiveVar that stores the current template selected in the workspace
It is used to help the Workspace Action Bar Promote/Demote buttons to indentify which paragraph is selected to do proper actions needed 
*/
interface PromoteDemoteWorkspaceTemplateContent {
    promoteDemoteWorkspaceTemplateData:
        | NormalizedDocumentTemplateViewItem
        | undefined
}
export const [
    promoteDemoteTemplateData,
    setPromoteDemoteWorkspaceTemplateData,
] = createReactiveVarStore<PromoteDemoteWorkspaceTemplateContent>({
    promoteDemoteWorkspaceTemplateData: undefined,
})
/*
promoteDemoteContent - 
It is a reactiveVar that stores the requirements needed to be able to promote or demote a paragraph
It is also the trigger to a useEffect in PropertyEditorEditableText component
*/
interface PromoteDemoteContent {
    promoteDemoteContent: PromoteDemoteRequirements | undefined
}
export const [
    promoteDemoteData,
    setPromoteDemoteContent,
] = createReactiveVarStore<PromoteDemoteContent>({
    promoteDemoteContent: undefined,
})

/*
isTemplateDataLastItem - 
It is a reactiveVar that stores the value of the currently selected paragraph's item order
It is used to validate the Move Down action in the Workspace Action Bar if the paragraph is not valid to move down because it is the last item
*/
interface WorkspaceTemplateFlags {
    isTemplateDataLastItem: boolean
}
export const [
    templateFlags,
    setIsTemplateDataLastItem,
] = createReactiveVarStore<WorkspaceTemplateFlags>({
    isTemplateDataLastItem: true,
})

export type MoveAction = PickActionButtonVariant<'MoveUp' | 'MoveDown'> | ''
/*
isMoveUpAndDownTriggered - 
It is a reactiveVar that stores the value of the current state of the move up and down if it is triggered through the Workspace Action Bar
action - 
It is used to pass in the action of the button triggered it is either moveup or movedown so the code will know which API to call
*/
export interface MoveUpAndDown {
    isMoveUpAndDownTriggered: boolean
    action: MoveAction
    isShortcutKey: boolean
}
export const [moveUpAndDown, setMoveUpAndDownTrigger] = createReactiveVarStore<
    MoveUpAndDown
>({
    isMoveUpAndDownTriggered: false,
    action: '',
    isShortcutKey: false,
})

/*
isCopyProductTypeTriggered - 
It is a reactiveVar that stores the value of the current state of the product type copy button if it is triggered through the project bar
It is also the trigger to a useEffect in DuplicateProductTypeButton component
*/
interface CopyProductType {
    isCopyProductTypeTriggered: boolean
}
export const [
    copyProductType,
    setCopyProductTypeTrigger,
] = createReactiveVarStore<CopyProductType>({
    isCopyProductTypeTriggered: false,
})

/* isPromoteDemoteTriggered - 
It is a reactiveVar that stores the value of the current state of the promote/demote if it is triggered through the project bar
*/
interface PromoteDemoteTriggered {
    isPromoteDemoteTriggered: boolean
}
export const [
    promoteDemoteTriggered,
    setPromoteDemoteTriggered,
] = createReactiveVarStore<PromoteDemoteTriggered>({
    isPromoteDemoteTriggered: false,
})

// newlyAddedParagraphText - sets the value when variant === newlyadded paragraph and will be emptied once the paragraph is saved
export const newlyAddedParagraphText = makeVar<string>('')

/**
 * `WorkspaceActions` is an interface that represents the actions available in the workspace.
 *
 * @property {boolean} showSMMActionButtons - Flag to show or hide SMM action buttons.
 * @property {boolean} isInsertPill - Flag to insert a pill.
 * @property {boolean} isInsertText - Flag to insert text.
 * @property {boolean} isInsertUOM - Flag to insert UOM.
 * @property {boolean} isAddSpecifierNote - Flag to add a specifier note.
 * @property {InsertPillHandler} insertPill - Handler to insert a pill.
 * @property {Partial<InsertPillProps>} insertPillRequirements - Requirements to insert a pill.
 * @property {InsertUOMPillProps} insertUomPill - Props to insert a UOM pill.
 * @property {AddNoteSpecifierHandler} addNoteSpecifier - Handler to add a note specifier.
 * @property {Partial<AddNoteSpecifierProps>} addNoteSpecifierRequirements - Requirements to add a note specifier.
 */
interface WorkspaceActions {
    showSMMActionButtons?: boolean
    isInsertPill: boolean
    isInsertText: boolean
    isInsertUOM: boolean
    isAddSpecifierNote: boolean
    insertPill?: InsertPillHandler
    insertPillRequirements: Partial<InsertPillProps>
    insertUomPill?: InsertUOMPillProps
    addNoteSpecifier?: AddNoteSpecifierHandler
    addNoteSpecifierRequirements: Partial<AddNoteSpecifierProps>
    omWorkspaceInsertPillHandler?: () => void
}
export const [
    reactiveWorkspaceActions,
    setReactiveWorkspaceActions,
] = createReactiveVarStore<WorkspaceActions>({
    showSMMActionButtons: processEnv.REACT_APP_ShowSMMActionButtons === '1',
    isInsertPill: false,
    isInsertText: false,
    isInsertUOM: false,
    isAddSpecifierNote: false,
    insertPillRequirements: {},
    addNoteSpecifierRequirements: {},
})

export const isWorkspaceActionsNotDefault = Object.values(
    reactiveWorkspaceActions?.() ?? {}
).some((value) => value === true || Object.keys(value).length > 0)
